import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import IngredientEditor, { serializeIngredients } from "../components/IngredientEditor.jsx";
import ProcedureEditor, { serializeSteps } from "../components/ProcedureEditor.jsx";
import { useCreateRecipe, useRecipesQuery } from "../hooks/useRecipesQuery.js";
import usePlan from "../hooks/usePlan.js";
import Button from "../components/ui/Button.jsx";
import Input from "../components/ui/Input.jsx";
import Select from "../components/ui/Select.jsx";
import Modal from "../components/ui/Modal.jsx";

const PLAN_LIMITS = { hobby: 1, pro: 10, enterprise: Infinity };

function UpgradeModal({ current, limit, onClose }) {
  return (
    <Modal open onClose={onClose} title="Recipe limit reached">
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        You've reached your Hobby plan limit ({current}/{limit} recipes).<br />
        To upgrade to Pro, email us at{" "}
        <a href="mailto:hello@fungistics.com" className="text-sky-700 dark:text-sky-400 underline">hello@fungistics.com</a>.
      </p>
      <Button onClick={onClose} className="w-full">Got it</Button>
    </Modal>
  );
}

const RECIPE_TYPES = [
  { value: "liquid_culture", label: "Liquid Culture" },
  { value: "bulk_spawn",     label: "Bulk Spawn" },
  { value: "substrate",      label: "Substrate" },
  { value: "other",          label: "Other" },
];

const EMPTY_ROW  = () => ({ name: "", amount: "", unit: "g" });
const EMPTY_STEP = () => ({ instruction: "", params: "" });

export default function RecipeNew() {
  const navigate = useNavigate();
  const createRecipe = useCreateRecipe();
  const { plan, planLoading } = usePlan();
  const { data: recipes } = useRecipesQuery();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const recipeCount = recipes?.length ?? 0;
  const limit = PLAN_LIMITS[plan ?? "hobby"] ?? 1;
  const atLimit = !planLoading && recipeCount >= limit;

  const [form, setForm] = useState({
    name: "",
    recipe_type: "substrate",
    notes: "",
  });
  const [ingredientRows, setIngredientRows] = useState([EMPTY_ROW()]);
  const [procedureSteps, setProcedureSteps] = useState([EMPTY_STEP()]);
  const [error, setError] = useState(null);

  function set(field) {
    return (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    try {
      const recipe = await createRecipe.mutateAsync({
        name:        form.name.trim(),
        recipe_type: form.recipe_type,
        ingredients: serializeIngredients(ingredientRows),
        procedure:   serializeSteps(procedureSteps),
        notes:       form.notes.trim() || null,
      });
      navigate(`/recipes/${recipe.id}`);
    } catch {
      setError("Failed to create recipe. Please try again.");
    }
  }

  return (
    <LayoutWide title="New Recipe">
      {showUpgrade && <UpgradeModal current={recipeCount} limit={limit} onClose={() => setShowUpgrade(false)} />}
      <div className="max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/recipes")} className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            ← Recipes
          </button>
          <h1 className="text-2xl font-semibold text-sky-800 dark:text-sky-300">New Recipe</h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-card border border-gray-200 dark:border-gray-700 p-6 flex flex-col gap-6">
          {error && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 rounded px-3 py-2">{error}</div>}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Name <span className="text-red-500 dark:text-red-400">*</span></label>
            <Input
              type="text"
              value={form.name}
              onChange={set("name")}
              placeholder="e.g. Rye Berry Bulk Spawn"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Type</label>
            <Select value={form.recipe_type} onChange={set("recipe_type")}>
              {RECIPE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Ingredients</label>
            <IngredientEditor rows={ingredientRows} onChange={setIngredientRows} />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Procedure</label>
            <ProcedureEditor steps={procedureSteps} onChange={setProcedureSteps} />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Notes</label>
            <textarea
              value={form.notes}
              onChange={set("notes")}
              rows={2}
              placeholder="Any additional notes or tips..."
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-y"
            />
          </div>

          {atLimit && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-400">
              You've reached your Hobby plan limit ({recipeCount}/{limit} recipes).{" "}
              <button type="button" onClick={() => setShowUpgrade(true)} className="underline font-medium">Learn more</button>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <Button
              type={atLimit ? "button" : "submit"}
              onClick={atLimit ? () => setShowUpgrade(true) : undefined}
              disabled={(!atLimit && (createRecipe.isPending || !form.name.trim())) || planLoading}
            >
              {planLoading ? "Loading…" : atLimit ? "Plan limit reached" : createRecipe.isPending ? "Saving…" : "Save Recipe"}
            </Button>
            <button
              type="button"
              onClick={() => navigate("/recipes")}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </LayoutWide>
  );
}
